import React, { useEffect, useState } from "react";
import { getMaturityLevel, maturityGap } from "./maturity";

const MaturityCard = ({ customerKey, secureShieldScore, riskAdjustedScore, previousSecureShieldScore, T }) => {
  const [selected, setSelected] = useState("riskAdjusted");
  const [focused, setFocused] = useState(null);

  useEffect(() => setSelected("riskAdjusted"), [customerKey]);

  const showingSecureShield = selected === "secureShield";
  const score = showingSecureShield ? secureShieldScore : riskAdjustedScore;
  const level = getMaturityLevel(score, T);
  const historicalChange = showingSecureShield
    && previousSecureShieldScore != null
    && secureShieldScore !== previousSecureShieldScore
    && (() => {
      const up = secureShieldScore > previousSecureShieldScore;
      const color = up ? T.green : T.red;
      const background = up ? "rgba(83,196,109,.12)" : "rgba(225,77,87,.12)";
      const border = up ? "rgba(83,196,109,.22)" : "rgba(225,77,87,.22)";
      const change = Math.round((secureShieldScore - previousSecureShieldScore) * 10) / 10;
      return <span data-testid="secure-shield-change" style={{ display:"inline-flex", alignItems:"center", gap:6, maxWidth:"100%", overflowWrap:"anywhere", fontWeight:700, fontSize:11, color, background, border:`1px solid ${border}`, borderRadius:999, padding:"4px 10px" }}>{up ? "▲" : "▼"} {up ? "+" : ""}{change}%</span>;
    })();

  const toggleStyle = value => ({
    border: `1px solid ${T.border}`,
    outline: focused === value ? `2px solid ${T.blue}` : "none",
    outlineOffset: 2,
    borderRadius:7,
    background: selected === value ? "rgba(68,166,255,.22)" : "transparent",
    color: selected === value ? T.text : T.muted,
    cursor:"pointer",
    minWidth:0,
    maxWidth:"100%",
    whiteSpace:"normal",
    fontFamily:"inherit",
    fontSize:11,
    fontWeight:600,
    padding:"3px 10px",
    minHeight:24,
  });

  return (
    <section data-testid="maturity-card" style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, padding:12, marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:12 }}>
        <div style={{ fontSize:10, color:T.muted, textTransform:"uppercase", letterSpacing:".06em", fontWeight:700 }}>Maturity</div>
        <div role="group" aria-label="Maturity score" style={{ display:"flex", flexWrap:"wrap", gap:4, maxWidth:"100%" }}>
          <button type="button" aria-pressed={selected === "riskAdjusted"} onClick={() => setSelected("riskAdjusted")} onFocus={() => setFocused("riskAdjusted")} onBlur={() => setFocused(null)} style={toggleStyle("riskAdjusted")}>Risk-Adjusted</button>
          <button type="button" aria-pressed={selected === "secureShield"} onClick={() => setSelected("secureShield")} onFocus={() => setFocused("secureShield")} onBlur={() => setFocused(null)} style={toggleStyle("secureShield")}>SecureShield</button>
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:4 }}>
        <div style={{ display:"flex", alignItems:"baseline", flexWrap:"wrap", gap:8, minWidth:0 }}>
          <span data-testid="maturity-level" style={{ fontSize:20, fontWeight:800, color:level.c }}>Level {level.n}</span>
          <span data-testid="maturity-label" style={{ fontSize:13, fontWeight:700 }}>{level.t}</span>
        </div>
        {historicalChange}
      </div>
      <div data-testid="maturity-basis" style={{ fontSize:11, color:T.muted }}>Based on {showingSecureShield ? "SecureShield" : "customer-adjusted"} score</div>
      <div aria-label={`${level.n} of 5 maturity segments`} style={{ display:"flex", gap:3, marginTop:8 }}>
        {[1,2,3,4,5].map(segment => <div key={segment} data-testid={`maturity-segment-${segment}`} style={{ flex:1, height:5, borderRadius:3, background: segment <= level.n ? level.c : "rgba(255,255,255,.08)" }} />)}
      </div>
      <div data-testid="maturity-gap" style={{ fontSize:11, color:T.muted, marginTop:6 }}>{maturityGap(score, T)}</div>
    </section>
  );
};

export default MaturityCard;
